/**
 * AircraftFullModal - Full deep-work view for aircraft inspection
 *
 * Supports two modes (persisted in store):
 * - "modal": centered Dialog (max-w-[1100px])
 * - "sidebar": right-side Sheet (~45% width) keeping map visible
 *
 * Toggle button in header switches between modes.
 */

import { PanelRight, Maximize2 } from 'lucide-react'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { AircraftTab } from './card-tabs/AircraftTab'
import { FinancialTab } from './card-tabs/FinancialTab'
import { FuelTab } from './card-tabs/FuelTab'
import { ClientTab } from './card-tabs/ClientTab'
import { ScheduleTab } from './card-tabs/ScheduleTab'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { TabId } from '@/stores/aircraftCardStore'

interface AircraftFullModalProps {
  enrichedData: Map<string, AllocationEnrichedData>
}

/**
 * Shared tab content rendered identically in both modal and sidebar
 */
function EnlargedTabContent({
  data,
  activeTab,
}: {
  data: AllocationEnrichedData
  activeTab: TabId
}) {
  return (
    <Tabs defaultValue={activeTab} className="flex-1 overflow-hidden flex flex-col">
      <TabsList className="w-full justify-start">
        <TabsTrigger value="aircraft">Aircraft</TabsTrigger>
        <TabsTrigger value="financial">Financial</TabsTrigger>
        <TabsTrigger value="fuel">Fuel</TabsTrigger>
        <TabsTrigger value="client">Client</TabsTrigger>
        <TabsTrigger value="schedule">Schedule</TabsTrigger>
      </TabsList>

      <div className="flex-1 overflow-y-auto mt-4 pr-1">
        <TabsContent value="aircraft" className="mt-0">
          <AircraftTab data={data} />
        </TabsContent>
        <TabsContent value="financial" className="mt-0">
          <FinancialTab data={data} />
        </TabsContent>
        <TabsContent value="fuel" className="mt-0">
          <FuelTab data={data} />
        </TabsContent>
        <TabsContent value="client" className="mt-0">
          <ClientTab data={data} />
        </TabsContent>
        <TabsContent value="schedule" className="mt-0">
          <ScheduleTab data={data} />
        </TabsContent>
      </div>
    </Tabs>
  )
}

/**
 * Header content shared between modal and sidebar
 */
function EnlargedHeader({ data }: { data: AllocationEnrichedData }) {
  const enlargedViewMode = useAircraftCardStore((s) => s.enlargedViewMode)
  const toggleEnlargedViewMode = useAircraftCardStore((s) => s.toggleEnlargedViewMode)

  const typeName = [data.aircraft.manufacturer, data.aircraft.model]
    .filter(Boolean)
    .join(' ') || data.aircraft.typeCode

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold">{data.aircraft.tailNumber}</span>
        <Badge variant="secondary" className="text-xs px-2 py-0.5">
          {data.aircraft.typeCode}
        </Badge>
        <span className="text-sm text-muted-foreground font-normal">{typeName}</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={toggleEnlargedViewMode}
        title={enlargedViewMode === 'modal' ? 'Switch to sidebar view' : 'Switch to modal view'}
      >
        {enlargedViewMode === 'modal' ? (
          <PanelRight className="w-4 h-4" />
        ) : (
          <Maximize2 className="w-4 h-4" />
        )}
      </Button>
    </div>
  )
}

export function AircraftFullModal({ enrichedData }: AircraftFullModalProps) {
  const cardState = useAircraftCardStore((s) => s.cardState)
  const closeFullModal = useAircraftCardStore((s) => s.closeFullModal)
  const enlargedViewMode = useAircraftCardStore((s) => s.enlargedViewMode)

  const selectedAllocationId = useAircraftEditorStore((s) => s.selectedAllocationId)

  const isOpen = cardState.mode === 'fullModal'
  const activeTab: TabId = cardState.mode === 'fullModal' ? cardState.activeTab : 'aircraft'

  const data = selectedAllocationId ? enrichedData.get(selectedAllocationId) : undefined

  if (!isOpen || !data) return null

  // Sidebar mode — Sheet from right
  if (enlargedViewMode === 'sidebar') {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => { if (!open) closeFullModal() }}>
        <SheetContent side="right" className="w-[45vw] min-w-[400px] max-w-[700px] overflow-hidden flex flex-col p-4">
          <SheetHeader className="pb-2">
            <SheetTitle asChild>
              <EnlargedHeader data={data} />
            </SheetTitle>
          </SheetHeader>
          <EnlargedTabContent data={data} activeTab={activeTab} />
        </SheetContent>
      </Sheet>
    )
  }

  // Modal mode — centered Dialog
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeFullModal() }}>
      <DialogContent className="sm:max-w-[1100px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle asChild>
            <EnlargedHeader data={data} />
          </DialogTitle>
        </DialogHeader>
        <EnlargedTabContent data={data} activeTab={activeTab} />
      </DialogContent>
    </Dialog>
  )
}
